import axios from "axios";

const DEFAULT_ADMIN_BASE = "https://api.chatgpt.sverhum.ru/chat/admin";
const ADMIN_BASE = process.env.REACT_APP_ADMIN_BASE || DEFAULT_ADMIN_BASE;

function withAdminHeader(adminToken) {
  return adminToken
    ? {
        headers: {
          "x-admin-token": adminToken
        }
      }
    : undefined;
}

export const adminApi = {
  generateCode() {
    return axios.get(`${ADMIN_BASE}/generate-code`);
  },

  verifyCode(code) {
    return axios.post(`${ADMIN_BASE}/verify-code`, { code });
  },

  getStats(adminToken) {
    return axios.post(`${ADMIN_BASE}/stats`, { adminToken });
  },

  getAnalytics(adminToken, force = false) {
    return axios.post(`${ADMIN_BASE}/analytics`, { adminToken, force });
  },

  getTriggersConfig(adminToken) {
    return axios.post(`${ADMIN_BASE}/triggers/get`, { adminToken });
  },

  saveTriggersConfig(adminToken, config) {
    return axios.post(`${ADMIN_BASE}/triggers/save`, { adminToken, config });
  },

  resetTriggersConfig(adminToken) {
    return axios.post(`${ADMIN_BASE}/triggers/reset`, { adminToken });
  },

  getTriggerRecordKinds(adminToken) {
    return axios.post(`${ADMIN_BASE}/trigger-records/kinds`, { adminToken });
  },

  getTriggerRecords(adminToken, kind, params = {}) {
    return axios.post(`${ADMIN_BASE}/trigger-records/list`, { adminToken, kind, ...(params || {}) });
  },

  getLocalesList(adminToken) {
    return axios.post(`${ADMIN_BASE}/locales/list`, { adminToken });
  },

  getLocale(adminToken, lang) {
    return axios.post(`${ADMIN_BASE}/locales/get`, { adminToken, lang });
  },

  updateLocale(adminToken, lang, data) {
    return axios.post(`${ADMIN_BASE}/locales/update`, { adminToken, lang, data });
  },

  getRouletteConfig(adminToken) {
    return axios.post(`${ADMIN_BASE}/roulette/get`, { adminToken });
  },

  saveRouletteConfig(adminToken, config) {
    return axios.post(`${ADMIN_BASE}/roulette/save`, { adminToken, config });
  },

  getPhotosessionConfig(adminToken) {
    return axios.post(`${ADMIN_BASE}/photosession/get`, { adminToken });
  },

  savePhotosessionConfig(adminToken, config) {
    return axios.post(`${ADMIN_BASE}/photosession/save`, { adminToken, config });
  },

  uploadPhotosessionPreview(adminToken, formData) {
    return axios.post(`${ADMIN_BASE}/photosession/upload-preview`, formData, withAdminHeader(adminToken));
  },

  getPhotoAnimationConfig(adminToken) {
    return axios.post(`${ADMIN_BASE}/photo-animation/get`, { adminToken });
  },

  savePhotoAnimationConfig(adminToken, config) {
    return axios.post(`${ADMIN_BASE}/photo-animation/save`, { adminToken, config });
  },

  uploadPhotoAnimationPreview(adminToken, formData) {
    return axios.post(`${ADMIN_BASE}/photo-animation/upload-preview`, formData, withAdminHeader(adminToken));
  },

  getTemplatesConfig(adminToken) {
    return axios.post(`${ADMIN_BASE}/templates/get`, { adminToken });
  },

  saveTemplatesConfig(adminToken, config) {
    return axios.post(`${ADMIN_BASE}/templates/save`, { adminToken, config });
  },

  searchUser(adminToken, id) {
    return axios.post(`${ADMIN_BASE}/search-user`, { adminToken, id });
  },

  runUserAction(adminToken, payload) {
    return axios.post(`${ADMIN_BASE}/user-action`, { ...payload, adminToken });
  },

  listSubscriptions(adminToken, params = {}) {
    return axios.post(`${ADMIN_BASE}/subscriptions/list`, { ...params, adminToken });
  },

  createPromoCode(adminToken, payload) {
    return axios.post(`${ADMIN_BASE}/promo-codes/create`, { ...payload, adminToken });
  },

  listPromoCodes(adminToken) {
    return axios.post(`${ADMIN_BASE}/promo-codes/list`, { adminToken });
  },

  deletePromoCode(adminToken, code) {
    return axios.post(`${ADMIN_BASE}/promo-codes/delete`, { adminToken, code });
  },

  getLogById(adminToken, logId) {
    return axios.post(`${ADMIN_BASE}/log-by-id`, { adminToken, logId });
  },

  getLogsByUser(adminToken, userId) {
    return axios.post(`${ADMIN_BASE}/logs-by-user`, { adminToken, userId });
  },

  getLogStats(adminToken) {
    return axios.post(`${ADMIN_BASE}/log-stats`, { adminToken });
  },

  getLastLogs(adminToken) {
    return axios.post(`${ADMIN_BASE}/last-logs`, { adminToken });
  },

  getRuntimeLogs(adminToken, params = {}) {
    return axios.post(`${ADMIN_BASE}/runtime-logs`, { adminToken, ...params });
  },

  getBroadcastPreview(adminToken, payload) {
    return axios.post(`${ADMIN_BASE}/broadcast-preview`, payload, withAdminHeader(adminToken));
  },

  getBroadcastStatus(adminToken, jobId) {
    return axios.post(
      `${ADMIN_BASE}/broadcast-status`,
      jobId ? { jobId } : {},
      withAdminHeader(adminToken)
    );
  },

  startBroadcast(adminToken, formData) {
    return axios.post(`${ADMIN_BASE}/broadcast`, formData, withAdminHeader(adminToken));
  },

  // ===== AI offers =====
  listAiOffers(adminToken, params = {}) {
    return axios.post(`${ADMIN_BASE}/ai-offers/list`, { ...params, adminToken });
  },

  getAiOffer(adminToken, id) {
    return axios.post(`${ADMIN_BASE}/ai-offers/get`, { adminToken, id });
  },

  createAiOffer(adminToken, payload) {
    return axios.post(`${ADMIN_BASE}/ai-offers/create`, { ...payload, adminToken });
  },

  updateAiOffer(adminToken, id, payload) {
    return axios.post(`${ADMIN_BASE}/ai-offers/update`, { ...payload, adminToken, id });
  },

  deleteAiOffer(adminToken, id) {
    return axios.post(`${ADMIN_BASE}/ai-offers/delete`, { adminToken, id });
  },

  bulkAiOffers(adminToken, action, ids) {
    return axios.post(`${ADMIN_BASE}/ai-offers/bulk`, { adminToken, action, ids });
  },

  previewAiOfferSimilarity(adminToken, offerId, text) {
    return axios.post(`${ADMIN_BASE}/ai-offers/preview-similarity`, { adminToken, offerId, text });
  },

  validateAiOffer(adminToken, payload) {
    return axios.post(`${ADMIN_BASE}/ai-offers/validate`, { ...payload, adminToken });
  },

  debugRankAiOffers(adminToken, params = {}) {
    return axios.post(`${ADMIN_BASE}/ai-offers/debug-rank`, { ...params, adminToken });
  },

  debugRuntimeAiOffers(adminToken, params = {}) {
    return axios.post(`${ADMIN_BASE}/ai-offers/debug-runtime`, { ...params, adminToken });
  },

  getAiOfferTimeseries(adminToken, offerId, days = 30) {
    return axios.post(`${ADMIN_BASE}/ai-offers/timeseries`, { adminToken, offerId, days });
  }
};

export default adminApi;
